/*
 * Cliente del incremento 5/6 · Presupuesto mensual.
 *
 * Usa la fábrica del núcleo (crearCliente) para NO reimplementar la cookie de
 * sesión, la envoltura común de respuesta ni el aviso de fin de sesión al shell,
 * y `guardarConConexion` en TODA escritura (D-02, RNF-052): sin red no se guarda
 * y no se promete guardar después.
 *
 * LO QUE ESTE MÓDULO NO HACE, Y ES CONTRATO (RNF-053):
 *   · No suma, no resta y no reordena. La resta de tres términos, el orden
 *     alfabético, los estados, las explicaciones y los conteos llegan RESUELTOS.
 *   · No declara `PROPAGADO_DE_OTRO_MES`: ese origen lo escribe únicamente la
 *     propagación, y el servidor rechaza al cliente que lo mande (RN-234).
 *   · No manda un booleano de alcance ni lo omite "porque casi siempre es
 *     conservar": es un enumerado obligatorio y SIN valor por defecto (RN-233c).
 */

import { crearCliente, guardarConConexion } from '../../../nucleo/api/cliente.js';

const api = crearCliente('/api/presupuesto');

/**
 * Los DOS valores del alcance de regeneración (D-156, D-157, RN-233c).
 *
 * NO ES UN BOOLEANO A PROPÓSITO: con un tercer alcance futuro, "ausente" y
 * "false" dejarían de significar lo mismo (mismo criterio que el 3/6 aplicó a
 * `renglonesRepartidos`). Ausente, vacío o desconocido → PARAMETRO_INVALIDO (422)
 * y no se escribe nada: nadie pierde trabajo manual por una omisión.
 */
export const ALCANCES = Object.freeze({
  conservarEditadoAMano: 'CONSERVAR_EDITADO_A_MANO',
  sustituirTodo: 'SUSTITUIR_TODO',
});

/**
 * Los TRES orígenes que el cliente puede declarar al guardar (RN-234).
 *
 * `PROPAGADO_DE_OTRO_MES` NO ESTÁ AQUÍ y no es un olvido: es una afirmación del
 * servidor sobre cómo llegó la cifra a ese mes, no una etiqueta elegible.
 * Editar una línea propagada tampoco es excepción: la cifra tecleada viaja como
 * CAPTURA_MANUAL y la pantalla NUNCA reenvía el origen que leyó (RN-227).
 */
export const ORIGENES = Object.freeze({
  capturaManual: 'CAPTURA_MANUAL',
  sugerenciaAnioAnterior: 'SUGERENCIA_ANIO_ANTERIOR',
  sugerenciaPromedio: 'SUGERENCIA_PROMEDIO',
});

/** Las cuatro bases de sugerencia que puede traer un renglón de §2. */
export const BASES = Object.freeze({
  anioAnterior: 'ANIO_ANTERIOR',
  promedio: 'PROMEDIO',
  sinHistoria: 'SIN_HISTORIA',
  sinSugerencia: 'SIN_SUGERENCIA',
});

function consulta(parametros) {
  const partes = Object.entries(parametros)
    .filter(([, valor]) => valor !== undefined && valor !== null && valor !== '')
    .map(([clave, valor]) => `${clave}=${encodeURIComponent(valor)}`);

  return partes.length > 0 ? `?${partes.join('&')}` : '';
}

/**
 * §1 · ConsultarPresupuestoDelMes (RF-101 a RF-103).
 * ES LA FUENTE ÚNICA DEL TABLERO: una sola llamada resuelve el mes (RNF-047).
 * `fechaLocal` es la del dispositivo y el servidor nunca la suple (D-06).
 */
export function consultarTablero({ idPeriodo, fechaLocal } = {}) {
  return api.obtener(`${consulta({ idPeriodo, fechaLocal })}`);
}

/**
 * §2 · ConsultarSugerenciasDePresupuesto (RF-099, RF-100). NO GUARDA NADA.
 * Devuelve la unión ya resuelta de las categorías que deben verse en el registro
 * de ese mes, en orden alfabético del servidor: la pantalla NO la cruza con la
 * del tablero ni la reordena (D-138, D-139).
 */
export function consultarSugerencias({ idPeriodo, fechaLocal } = {}) {
  return api.obtener(`/sugerencias${consulta({ idPeriodo, fechaLocal })}`);
}

/**
 * §3 · GuardarPresupuestoDelMes (RF-097, RF-098). En lote y en una sola
 * transacción: o entran todas las líneas o no entra ninguna (RN-034).
 *
 * Devuelve el tablero completo MÁS `categoriasConImporteCambiado` y
 * `mesesSiguientesDelAnio`, que es lo que dispara la pregunta de propagación. La
 * pantalla no deduce por su cuenta cuándo preguntar (D-151, RN-230b).
 */
export function guardarLineasDelMes(idPeriodo, { lineas, fechaLocal }) {
  return guardarConConexion(() => api.actualizar(`/${idPeriodo}/lineas`, { lineas, fechaLocal }));
}

/**
 * §4 · EliminarLineaDePresupuesto (RF-106). BAJA LÓGICA (RN-209): la categoría
 * pasa al bloque de gasto sin presupuesto. Sin `confirmado` el servidor responde
 * CONFIRMACION_REQUERIDA con el texto que hay que enseñar.
 */
export function eliminarLinea(idPresupuestoDeCategoria, { confirmado = false, version, fechaLocal } = {}) {
  return guardarConConexion(() => api.eliminar(
    `/lineas/${idPresupuestoDeCategoria}${consulta({ confirmado, version, fechaLocal })}`,
  ));
}

/** §4 bis · ConsultarPorcentajeAnualDeIncremento. Un año sin fila responde 200 con nulo. */
export function consultarPorcentajeAnual(anio) {
  return api.obtener(`/porcentaje-anual/${anio}`);
}

/**
 * §4 bis · FijarPorcentajeAnualDeIncremento (RF-109, D-123).
 * `confirmadoAlcanceAnual` es obligatorio: fijar un número que afecta a los doce
 * meses no puede pasar por accidente (RN-217e).
 */
export function fijarPorcentajeAnual(anio, { porcentajeIncremento, confirmadoAlcanceAnual, version } = {}) {
  return guardarConConexion(() => api.actualizar(`/porcentaje-anual/${anio}`, {
    porcentajeIncremento, confirmadoAlcanceAnual, version,
  }));
}

/**
 * §4 ter · GenerarPresupuestoDelAnio (RF-110, RN-229, RN-233).
 *
 * SE INVOCA DOS VECES: primero con `soloVistaPrevia: true` —que no escribe nada y
 * devuelve LOS DOS alcances— y después con `soloVistaPrevia: false`,
 * `confirmado: true` y el alcance elegido. Nada se genera al consultar, al
 * navegar entre meses ni al abrir la aplicación.
 *
 * `porcentajeIncremento` AUSENTE significa "no cambio el porcentaje de este año",
 * NO "cero" (D-162, RN-216 bis): omitirlo nunca borra un porcentaje fijado.
 */
export function generarPresupuestoDelAnio(anio, peticion) {
  const ruta = `/anios/${anio}/generacion`;
  if (peticion?.soloVistaPrevia) return api.publicar(ruta, peticion);
  return guardarConConexion(() => api.publicar(ruta, peticion));
}

/**
 * §4 quater · PropagarPresupuestoAMesesSiguientes (RF-111, RN-230).
 *
 * ES OPT-IN Y NO SE INVOCA NUNCA SOLA (RN-232): solo cuando la persona pulsa
 * "Aplicar a los meses siguientes". Cerrar la pregunta o pulsar Escape NO la
 * invoca; el silencio nunca propaga. Escribe EL MISMO IMPORTE, no la diferencia,
 * y solo en meses posteriores del MISMO año (RN-230c, RN-230d).
 */
export function propagarAMesesSiguientes(idPeriodo, peticion) {
  const ruta = `/${idPeriodo}/propagacion`;
  if (peticion?.soloVistaPrevia) return api.publicar(ruta, peticion);
  return guardarConConexion(() => api.publicar(ruta, peticion));
}
